<template>
    <div class="opModal modal-wide">
        <!-- Header -->
        <div class="d-flex justify-content-between align-items-center mb-3">
            <h5 class="text-red mb-0">
                <icon-buildings class="me-2" style="width: 18px; height: 18px;" />
                {{title}}
            </h5>
            <button class="btn btn-sm" @click="$emit('close')">
                <icon-x-circle />
            </button>
        </div>

        <!-- Two Column Layout: Filters on Left, Table on Right -->
        <div class="row g-3">
            <!-- Left Column: Filters -->
            <div class="col-lg-3">
                <div class="filters-column">
                    <h6 class="mb-3 fw-bold">Filtri di ricerca</h6>

                    <div class="form-group mb-3">
                        <label class="form-label small">Città</label>
                        <input type="text" class="form-control form-control-sm" v-model="search.citta" />
                    </div>

                    <div class="form-group mb-3">
                        <label class="form-label small">Numero immobile</label>
                        <input type="text" class="form-control form-control-sm" v-model="search.numeroImmobile" />
                    </div>

                    <div class="form-group mb-3">
                        <label class="form-label small">Solo cantieri</label>
                        <div>
                            <toggler v-model="search.stammobjektOnly"></toggler>
                        </div>
                    </div>

                    <div class="form-group mb-3">
                        <label class="form-label small">Indirizzo</label>
                        <input type="text" class="form-control form-control-sm" v-model="search.indirizzo" />
                    </div>

                    <div class="form-group mb-3">
                        <label class="form-label small">Zona</label>
                        <input type="text" class="form-control form-control-sm" v-model="search.zona" />
                    </div>

                    <div class="form-group mb-3">
                        <label class="form-label small">Creato dal</label>
                        <op-date class="form-control form-control-sm" v-model="search.createdFrom"></op-date>
                    </div>

                    <div class="form-group mb-3">
                        <label class="form-label small">Creato al</label>
                        <op-date class="form-control form-control-sm" v-model="search.createdTo"></op-date>
                    </div>

                </div>
            </div>

            <!-- Right Column: Table -->
            <div class="col-lg-9">
                <!-- ultimi immobili usati: scelta rapida -->
                <div class="mb-3" v-if="recentEstates.length">
                    <div class="small fw-bold mb-2">Ultimi immobili usati</div>
                    <div class="d-flex flex-wrap gap-2">
                        <button type="button" class="btn btn-sm btn-outline-primary" v-for="r in recentEstates" :key="r.estateId" @click="choose(r)">
                            {{r.description}}
                        </button>
                    </div>
                </div>
                <div>
                    <div class="table-container">
                        <table class="table table-hover table-sm">
                            <thead class="table-light sticky-header">
                                <tr>
                                    <th>Data creazione</th>
                                    <th>Città</th>
                                    <th>Numero immobile</th>
                                    <th>Indirizzo</th>
                                    <th>Cantiere</th>
                                    <th>Sup. comm.</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-if="list.data.length == 0">
                                    <td colspan="6" class="text-center text-muted py-4">
                                        Nessuna riga da mostrare
                                    </td>
                                </tr>
                                <tr class="pointer" v-for="item in list.data" :key="item.id" @click="chooseRow(item)">
                                    <td>{{item.createdOn}}</td>
                                    <td>{{item.citta}}</td>
                                    <td>{{item.numeroImmobile}}</td>
                                    <td>{{item.indirizzo}} {{item.numeroCivico}}</td>
                                    <td>{{item.isStammobjekt != 0 ? 'Cantiere' : ''}}</td>
                                    <td>{{item.superficieCommerciale}} mq</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <!-- Pagination -->
                    <div class="d-flex justify-content-center mt-3">
                        <button type="button" class="btn btn-sm btn-outline-primary me-2" :disabled="page == 1" @click="prev">Prec.</button>
                        <button type="button" class="btn btn-sm btn-outline-secondary me-1" v-for="p in prevPages" :key="'prev-'+p" @click="setPage(p)">{{p}}</button>
                        <button type="button" class="btn btn-sm btn-primary">{{page}}</button>
                        <button type="button" class="btn btn-sm btn-outline-secondary ms-1" v-for="p in nextPages" :key="'next-'+p" @click="setPage(p)">{{p}}</button>
                        <button type="button" class="btn btn-sm btn-outline-primary ms-2" :disabled="totalPages <= page" @click="next">Succ.</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import * as OM from '@/model';
import { OnOfficeClient, RecentEstatesClient } from '@/services/Services';

/**
 * Scelta di un immobile onOffice: dagli ultimi usati o dalla ricerca. Un clic sceglie e chiude.
 */
@Options({
    emits: ['close']
})
export default class selectEstatesModal extends Vue {

    @Prop({ default: "Scegli l'immobile" }) title: string;
    @Prop() callback: (selection: OM.EstateSelection) => void;

    search: OM.SearchEstatePagedVm = new OM.SearchEstatePagedVm();
    recentEstates: OM.EstateSelection[] = [];

    list: OM.OnOfficePagedResponseOfOnOfficePagedEstateRow = new OM.OnOfficePagedResponseOfOnOfficePagedEstateRow();
    page: number = 1;
    count: number = 25;

    created(){
        this.init();
        RecentEstatesClient.getMine(false)
        .then(x => this.recentEstates = x);
    }

    init(){
        let vm = { ...this.search };
        vm.page = this.page;
        vm.count = this.count;

        OnOfficeClient.getPagedEstates(vm)
        .then(x => {
            this.list = x;
        })
        .catch(error => {
        });
    }

    get totalPages(){
        return Math.ceil(this.list.total / this.count);
    }

    get prevPages(){
        let ris = [];
        for(let c = 1; c <= 3; c++){
            let toAdd = this.page - c;
            if(toAdd > 0){
                ris.push(toAdd);
            }
        }
        return ris.reverse();
    }

    get nextPages(){
        let ris = [];
        for(let c = 1; c <= 3; c++){
            let toAdd = this.page + c;
            if(toAdd <= this.totalPages){
                ris.push(toAdd);
            }
        }
        return ris;
    }

    next(){
        if(this.totalPages <= this.page)
            return
        this.setPage(this.page + 1);
    }

    prev(){
        if(this.page <= 1)
            return;
        this.setPage(this.page - 1);
    }

    setPage(val){
        this.page = val;
        this.init();
    }

    debuncio: any = null;
    @Watch('search', { deep: true })
    onSearchChange(next, prev){
        clearTimeout(this.debuncio);
        this.debuncio = setTimeout(() => {
            this.page = 1;
            this.init();
        }, 600);
    }

    chooseRow(item: OM.OnOfficePagedEstateRow){
        const selection = new OM.EstateSelection();
        selection.estateId = item.id;
        // descrizione salvata nel progetto e negli ultimi usati: per riconoscere l'immobile senza richiamare onOffice
        const address = [item.indirizzo, item.numeroCivico].filter(x => x).join(' ');
        selection.description = [item.numeroImmobile ? 'Rif. ' + item.numeroImmobile : '', [address, item.citta].filter(x => x).join(', ')]
            .filter(x => x).join(' · ');
        this.choose(selection);
    }

    choose(selection: OM.EstateSelection){
        this.callback(selection);
        this.$emit('close');
    }

}
</script>
<style scoped>
.modal-wide {
    max-width: 95vw;
    width: 95vw;
}

.filters-column {
    position: sticky;
    top: 0;
    max-height: calc(100vh - 200px);
    overflow-y: auto;
    padding-right: 0.5rem;
}

.filters-column h6 {
    font-size: 0.95rem;
}

.table-container {
    max-height: calc(100vh - 300px);
    overflow-y: auto;
    overflow-x: auto;
}

.sticky-header th {
    position: sticky;
    top: 0;
    background-color: #f8f9fa;
    z-index: 10;
    box-shadow: 0 2px 2px -1px rgba(0, 0, 0, 0.1);
}

.pointer {
    cursor: pointer;
}

.table-hover tbody tr:hover {
    background-color: rgba(0, 123, 255, 0.1);
}

.form-control-sm,
.form-label {
    font-size: 0.875rem;
}

.btn-sm {
    font-size: 0.875rem;
}
</style>